import React, { useState } from 'react';
import { DashboardWidget, DashboardConfig } from '../services/dashboardConfigService';

interface DashboardBuilderProps {
  config: DashboardConfig;
  onConfigChange: (config: DashboardConfig) => void;
  onClose: () => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
}

const WIDGET_TYPES = [
  { type: 'kpi', label: 'KPI Card', description: 'Display a single metric' },
  { type: 'bar', label: 'Bar Chart', description: 'Vertical bar chart' },
  { type: 'donut', label: 'Donut Chart', description: 'Pie/Donut chart' },
  { type: 'area', label: 'Area Chart', description: 'Area chart' },
  { type: 'line', label: 'Line Chart', description: 'Line chart' },
  { type: 'table', label: 'Table', description: 'Data table' },
] as const;

const KPI_TYPES = [
  { id: 'total_referrals', label: 'Total Referrals' },
  { id: 'escalation_count', label: 'Escalations' },
  { id: 'edited_count', label: 'Edited' },
  { id: 'unedited_count', label: 'Unedited' },
  { id: 'category_1', label: 'Category 1' },
  { id: 'category_2', label: 'Category 2' },
  { id: 'category_3', label: 'Category 3' },
  { id: 'category_4', label: 'Category 4' },
] as const;

const CHART_DATA_SOURCES = [
  { id: 'category_distribution', label: 'Category Distribution' },
  { id: 'edited_vs_unedited', label: 'Edited vs Unedited' },
] as const;

export const DashboardBuilder: React.FC<DashboardBuilderProps> = ({
  config,
  onConfigChange,
  onClose,
  onSave,
  isSaving,
}) => {
  const [editingWidget, setEditingWidget] = useState<string | null>(null);
  const [showAddWidget, setShowAddWidget] = useState(false);

  const addWidget = (type: DashboardWidget['type']) => {
    const newWidget: DashboardWidget = {
      id: `widget-${Date.now()}`,
      type,
      title: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      config: {},
    };

    if (type === 'kpi') {
      newWidget.config = { metric: 'total_referrals' };
    } else if (type === 'bar' || type === 'area' || type === 'line') {
      newWidget.config = { dataSource: 'category_distribution' };
    } else if (type === 'donut') {
      newWidget.config = { dataSource: 'category_distribution' };
    }

    const updatedConfig: DashboardConfig = {
      ...config,
      widgets: [...(config.widgets || []), newWidget],
    };
    onConfigChange(updatedConfig);
    setShowAddWidget(false);
    setEditingWidget(newWidget.id);
  };

  const removeWidget = (widgetId: string) => {
    const updatedConfig: DashboardConfig = {
      ...config,
      widgets: (config.widgets || []).filter(w => w.id !== widgetId),
    };
    onConfigChange(updatedConfig);
    if (editingWidget === widgetId) {
      setEditingWidget(null);
    }
  };

  const updateWidget = (widgetId: string, updates: Partial<DashboardWidget>) => {
    const updatedConfig: DashboardConfig = {
      ...config,
      widgets: (config.widgets || []).map(w =>
        w.id === widgetId ? { ...w, ...updates } : w
      ),
    };
    onConfigChange(updatedConfig);
  };

  const moveWidget = (widgetId: string, direction: 'up' | 'down') => {
    const widgets = [...(config.widgets || [])];
    const index = widgets.findIndex(w => w.id === widgetId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= widgets.length) return;

    [widgets[index], widgets[newIndex]] = [widgets[newIndex], widgets[index]];

    const updatedConfig: DashboardConfig = {
      ...config,
      widgets,
    };
    onConfigChange(updatedConfig);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '1200px', maxHeight: '90vh', overflow: 'auto' }}
      >
        <div className="modal-header">
          <h3>Dashboard Builder</h3>
          <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
            <button
              className="btn btn-primary"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Dashboard'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="modal-body">
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4>Widgets ({config.widgets?.length || 0})</h4>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Add, remove, and configure widgets for your dashboard
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddWidget(!showAddWidget)}
            >
              + Add Widget
            </button>
          </div>

          {showAddWidget && (
            <div
              style={{
                border: '2px dashed #ddd',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                backgroundColor: '#f9fafb',
              }}
            >
              <h5 style={{ marginBottom: '15px' }}>Select Widget Type</h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                {WIDGET_TYPES.map((widgetType) => (
                  <button
                    key={widgetType.type}
                    onClick={() => addWidget(widgetType.type)}
                    style={{
                      padding: '15px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#E84B37';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(232, 75, 55, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ fontWeight: '600', marginBottom: '5px' }}>{widgetType.label}</div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>{widgetType.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {config.widgets && config.widgets.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {config.widgets.map((widget, index) => (
                <div
                  key={widget.id}
                  style={{
                    border: editingWidget === widget.id ? '2px solid #E84B37' : '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: editingWidget === widget.id ? '#fff5f5' : 'white',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontWeight: '600' }}>
                        {index + 1}. {widget.title} ({widget.type})
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => moveWidget(widget.id, 'up')}
                        disabled={index === 0}
                        style={{ padding: '4px 8px', fontSize: '0.8rem' }}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => moveWidget(widget.id, 'down')}
                        disabled={index === config.widgets!.length - 1}
                        style={{ padding: '4px 8px', fontSize: '0.8rem' }}
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => setEditingWidget(editingWidget === widget.id ? null : widget.id)}
                        style={{ padding: '4px 8px', fontSize: '0.8rem' }}
                      >
                        {editingWidget === widget.id ? 'Done' : 'Edit'}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => removeWidget(widget.id)}
                        style={{ padding: '4px 8px', fontSize: '0.8rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {editingWidget === widget.id && (
                    <div style={{ marginTop: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #eee' }}>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                          Widget Title:
                        </label>
                        <input
                          type="text"
                          value={widget.title}
                          onChange={(e) => updateWidget(widget.id, { title: e.target.value })}
                          className="form-control"
                          style={{ width: '100%' }}
                        />
                      </div>

                      {widget.type === 'kpi' && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Metric:
                          </label>
                          <select
                            value={widget.config.metric || 'total_referrals'}
                            onChange={(e) => updateWidget(widget.id, { config: { ...widget.config, metric: e.target.value } })}
                            className="form-control"
                            style={{ width: '100%' }}
                          >
                            {KPI_TYPES.map((kpi) => (
                              <option key={kpi.id} value={kpi.id}>
                                {kpi.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {(widget.type === 'bar' || widget.type === 'area' || widget.type === 'line' || widget.type === 'donut') && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Data Source:
                          </label>
                          <select
                            value={widget.config.dataSource || 'category_distribution'}
                            onChange={(e) => updateWidget(widget.id, { config: { ...widget.config, dataSource: e.target.value } })}
                            className="form-control"
                            style={{ width: '100%' }}
                          >
                            {CHART_DATA_SOURCES.map((source) => (
                              <option key={source.id} value={source.id}>
                                {source.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p>No widgets yet. Click "Add Widget" to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

